import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Separator } from '@creanizer/ui'
import { Save, Copy, Star, Edit } from 'lucide-react'
import type { SkillResult as SkillResultType } from '@creanizer/types'
import { SkillPerformanceVisual } from './SkillPerformanceVisual'
import { SkillEditModal } from './SkillEditModal'

interface SkillResultProps {
  result: SkillResultType
  onSave?: () => void
  onEdit?: (updatedResult: Partial<SkillResultType>) => void
}

export function SkillResult({ result, onSave, onEdit }: SkillResultProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }
  
  return (
    <div className="card">
      <div className="mb-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <h3 className="text-2xl font-semibold">{result.basicInfo.name}</h3>
            <div className="flex flex-wrap gap-2">
              <Badge>{result.basicInfo.type}</Badge>
              <Badge variant="outline">{result.basicInfo.category}</Badge>
              <Badge variant="secondary">{result.basicInfo.tier}</Badge>
              <Badge variant="secondary">{result.basicInfo.rarity}</Badge>
              <Badge variant="outline">Lv.{result.basicInfo.level}</Badge>
            </div>
          </div>
          <div className="flex gap-2">
            {onEdit && (
              <Button onClick={() => setIsEditModalOpen(true)} size="sm" variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                編集
              </Button>
            )}
            {onSave && (
              <Button onClick={onSave} size="sm" variant="outline">
                <Save className="w-4 h-4 mr-2" />
                保存
              </Button>
            )}
          </div>
        </div>
        <p className="text-muted-foreground mt-2">{result.basicInfo.summary}</p>
      </div>
      
      <div>
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">基本情報</TabsTrigger>
            <TabsTrigger value="effects">効果</TabsTrigger>
            <TabsTrigger value="performance">演出</TabsTrigger>
            <TabsTrigger value="evolution">進化</TabsTrigger>
            <TabsTrigger value="lore">伝承</TabsTrigger>
          </TabsList>
          
          <TabsContent value="basic" className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">説明</h4>
              <p className="text-sm">{result.basicInfo.description}</p>
            </div>
            
            {result.basicInfo.flavorText && (
              <div>
                <h4 className="font-semibold mb-2">フレーバーテキスト</h4>
                <p className="text-sm italic text-muted-foreground">{result.basicInfo.flavorText}</p>
              </div>
            )}
            
            {result.basicInfo.alternativeNames?.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">別名</h4>
                <div className="flex flex-wrap gap-2">
                  {result.basicInfo.alternativeNames.map((name, i) => (
                    <Badge key={i} variant="outline">{name}</Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div>
              <h4 className="font-semibold mb-2">タグ</h4>
              <div className="flex flex-wrap gap-2">
                {result.basicInfo.tags.map((tag, i) => (
                  <Badge key={i} variant="secondary">{tag}</Badge>
                ))}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="effects" className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">主要効果</h4>
              <div className="space-y-2">
                <Badge>{result.effects.primaryEffect.type}</Badge>
                <p className="text-sm">{result.effects.primaryEffect.description}</p>
                <dl className="text-sm space-y-1">
                  <div className="flex">
                    <dt className="font-medium w-24">威力:</dt>
                    <dd>{result.effects.primaryEffect.potency}</dd>
                  </div>
                  {result.effects.primaryEffect.element && (
                    <div className="flex">
                      <dt className="font-medium w-24">属性:</dt>
                      <dd>{result.effects.primaryEffect.element}</dd>
                    </div>
                  )}
                  {result.effects.primaryEffect.duration && (
                    <div className="flex">
                      <dt className="font-medium w-24">持続時間:</dt>
                      <dd>{result.effects.primaryEffect.duration}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
            
            {result.effects.secondaryEffects && result.effects.secondaryEffects.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">副次効果</h4>
                <div className="space-y-2">
                  {result.effects.secondaryEffects.map((effect, i) => (
                    <div key={i} className="border-l-2 pl-3">
                      <Badge variant="outline">{effect.type}</Badge>
                      <p className="text-sm mt-1">{effect.description}</p>
                      <p className="text-xs text-muted-foreground">
                        発生率: {effect.chance} {effect.condition && `(${effect.condition})`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {result.effects.specialEffects && result.effects.specialEffects.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">特殊効果</h4>
                <div className="space-y-2">
                  {result.effects.specialEffects.map((effect, i) => (
                    <div key={i} className="text-sm">
                      <p><span className="font-medium">トリガー:</span> {effect.trigger}</p>
                      <p>{effect.effect}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {result.effects.synergyEffects && result.effects.synergyEffects.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">シナジー効果</h4>
                <div className="space-y-2">
                  {result.effects.synergyEffects.map((effect, i) => (
                    <div key={i} className="text-sm">
                      <p><span className="font-medium">連携スキル:</span> {effect.withSkill}</p>
                      <p>{effect.effect}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="performance">
            <SkillPerformanceVisual performance={result.performance} />
          </TabsContent>
          
          <TabsContent value="evolution" className="space-y-4">
            {result.evolution.masteryLevels && result.evolution.masteryLevels.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">熟練度レベル</h4>
                <div className="space-y-2">
                  {result.evolution.masteryLevels.map((level, i) => (
                    <div key={i} className="border-l-2 pl-3">
                      <div className="flex items-center gap-2">
                        <Badge>{level.level}</Badge>
                        <span className="font-medium text-sm">{level.name}</span>
                      </div>
                      <ul className="text-sm text-muted-foreground mt-1">
                        {level.improvements.map((imp, j) => (
                          <li key={j}>• {imp}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {result.evolution.evolutionPaths && result.evolution.evolutionPaths.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">進化パス</h4>
                <div className="space-y-2">
                  {result.evolution.evolutionPaths.map((path, i) => (
                    <div key={i} className="border rounded-lg p-3">
                      <p className="font-medium">{path.name}</p>
                      <p className="text-sm text-muted-foreground mt-1">{path.description}</p>
                      {path.changes && (
                        <div className="mt-2 text-sm">
                          <p className="font-medium">変更点:</p>
                          <ul className="text-muted-foreground">
                            <li>• 威力増加: {path.changes.powerIncrease}</li>
                            {path.changes.newEffects?.map((effect, j) => (
                              <li key={j}>• 新効果: {effect}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {result.evolution.mutations && result.evolution.mutations.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">変異</h4>
                <div className="space-y-2">
                  {result.evolution.mutations.map((mutation, i) => (
                    <div key={i} className="text-sm">
                      <p className="font-medium">{mutation.name}</p>
                      <p className="text-muted-foreground">{mutation.description}</p>
                      <p className="text-xs">発生率: {mutation.chance}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="lore" className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">起源</h4>
              <dl className="text-sm space-y-1">
                <div className="flex">
                  <dt className="font-medium w-24">創始者:</dt>
                  <dd>{result.lore.origin.creator}</dd>
                </div>
                <div className="flex">
                  <dt className="font-medium w-24">時代:</dt>
                  <dd>{result.lore.origin.era}</dd>
                </div>
                <div className="flex">
                  <dt className="font-medium w-24">場所:</dt>
                  <dd>{result.lore.origin.location}</dd>
                </div>
              </dl>
              <p className="text-sm mt-2">{result.lore.origin.circumstances}</p>
            </div>
            
            {result.lore.historicalSignificance?.famousUsers && result.lore.historicalSignificance.famousUsers.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">著名な使用者</h4>
                <div className="space-y-2">
                  {result.lore.historicalSignificance.famousUsers.map((user, i) => (
                    <div key={i} className="text-sm">
                      <p className="font-medium">{user.name} ({user.era})</p>
                      <p className="text-muted-foreground">{user.achievement}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {result.lore.culturalImpact && (
              <div>
                <h4 className="font-semibold mb-2">文化的影響</h4>
                <div className="text-sm">
                  <p className="mb-1">象徴: {result.lore.culturalImpact.symbolism}</p>
                  {result.lore.culturalImpact.traditions && (
                    <div>
                      <p className="font-medium">伝統:</p>
                      <ul className="text-muted-foreground">
                        {result.lore.culturalImpact.traditions.map((tradition, i) => (
                          <li key={i}>• {tradition}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {result.lore.quotes && result.lore.quotes.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2">名言</h4>
                <div className="space-y-2">
                  {result.lore.quotes.map((quote, i) => (
                    <blockquote key={i} className="border-l-2 pl-3 italic">
                      <p className="text-sm">"{quote.text}"</p>
                      <footer className="text-xs text-muted-foreground mt-1">
                        — {quote.speaker}
                      </footer>
                    </blockquote>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      
      {isEditModalOpen && onEdit && (
        <SkillEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={(updatedResult) => {
            onEdit(updatedResult)
            setIsEditModalOpen(false)
          }}
          skillResult={result}
          title={`「${result.basicInfo.name}」を編集`}
        />
      )}
    </div>
  )
}